
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import {
    Grid,
    Typography,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Link,
} from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import PageviewIcon from '@material-ui/icons/Pageview';
import ViewListIcon from '@material-ui/icons/ViewList';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import LineGraph from '../common/LineGraph/LineGraph';
import CompanyStatsForm from './CompanyStatsForm';
import EnhancedTable from '../common/EnhancedTable/EnhancedTable';
import urlBuilder from '../../utils/urlBuilder';
import DoughnutGraph from '../common/DoughnutGraph/DoughnutGraph';
import routeConstants from '../../Routes/routeConstants';
import trackingUtils from '../../utils/trackingUtils';
import graphKeysPropTypes from '../../proptypes/graphKeysPropTypes';
import suggestionsPropType from '../../proptypes/suggestionsPropType';
import translationPropType from '../../proptypes/translationPropType';
import companyStatsFormStatePropTypes from '../../proptypes/companyStatsFormStatePropTypes';

const styles = theme => ({
    root: {
        flexGrow: 1,
        padding: theme.spacing(2),
        maxWidth: '100%',
    },
    listRoot: {
        width: '100%',
    },
    cardOverflowVisible: {
        overflow: 'visible',
    },
});

const CompanyStatsWrapper = (props) => {
    const {
        classes,
        hidMap,
        width,
        handleClear,
        graphData,
        selectedSuggestions,
        handleSelectCompanySuggestions,
        handleSelectedLeadsGraphKeys,
        handleSelectedClicksGraphKeys,
        handleSelectedImpressionsGraphKeys,
        graphKeysLead,
        preSelectedGraphKeysLead,
        graphKeysClick,
        preSelectedGraphKeysClick,
        graphKeysImpression,
        preSelectedGraphKeysImpression,
        companyTableData,
        productTableData,
        leadOriginData,
        handleSubmit,
        handleUpdateFormField,
        formState,
        handleGroupBy,
        groupBy,
        userPreferedLanguage,
        totalImpressionCount,
        totalDetailImpressionCount,
        totalListImpressionCount,
        totalClickCount,
        totalListClickCount,
        totalListCTR,
        totalLeadCount,
        translation,
        isAnalyzeUser,
    } = props;

    useEffect(() => {
        trackingUtils.trackPageview(routeConstants.COMPANY_STATS);
    }, []);

    useEffect(() => {
        document.title = translation.COMPANY_ANALYSIS_PAGE_TITLE;
    }, [translation]);

    const searchSelectedCompanies = (selectedIds) => {
        const newFormState = Object.assign({}, formState, { orgNo: '' }, { hid: selectedIds.join(',') });
        handleSubmit(newFormState);
    };

    const kpis = [
        {
            key: 'IMPRESSION_OF_DETAILPAGE',
            number: totalDetailImpressionCount,
            text: translation.IMPRESSION_OF_DETAILPAGE,
            icon: (<PageviewIcon fontSize="large" />),

        },
        {
            key: 'IMPRESSION_IN_LIST',
            number: totalListImpressionCount,
            text: translation.IMPRESSION_IN_LIST,
            icon: (<ViewListIcon fontSize="large" />),

        },
        {
            key: 'CLICK_IN_LIST',
            number: totalListClickCount,
            text: translation.CLICK_IN_LIST,
            icon: (<TouchAppIcon fontSize="large" />),

        },
        {
            key: 'CTR_IN_LIST',
            number: totalListCTR,
            text: translation.CTR_IN_LIST,
            icon: (<TouchAppIcon fontSize="large" />),

        },
    ];

    const graphs = [
        {
            key: 'LEADS',
            header: translation.LEADS,
            totalCount: totalLeadCount,
            handleSelectGraphKeys: handleSelectedLeadsGraphKeys,
            graphKeys: graphKeysLead,
            selectedGraphKeys: preSelectedGraphKeysLead,
            graphData: graphData ? graphData.lead : {},
        },
        {
            key: 'CLICKS',
            header: translation.CLICK,
            totalCount: totalClickCount,
            handleSelectGraphKeys: handleSelectedClicksGraphKeys,
            graphKeys: graphKeysClick,
            selectedGraphKeys: preSelectedGraphKeysClick,
            graphData: graphData ? graphData.click : {},
        },
        {
            key: 'IMPRESSIONS',
            header: translation.IMPRESSIONS,
            totalCount: totalImpressionCount,
            handleSelectGraphKeys: handleSelectedImpressionsGraphKeys,
            graphKeys: graphKeysImpression,
            selectedGraphKeys: preSelectedGraphKeysImpression,
            graphData: graphData ? graphData.impression : {},
        },
    ];

    const getHeader = (companyCount) => {
        const firstHid = Object.keys(hidMap)[0];
        const firstCompany = Object.values(hidMap)[0];

        return companyCount === 1
            ? (
                <>
                    {translation.STATISTICS_FOR}
                    <Link
                        href={urlBuilder.getHittaCompanyUrl(firstHid)}
                        target="_blank"
                    >
                        {` ${firstCompany.name}, ${firstCompany.city}`}
                    </Link>
                </>
            )
            : `${translation.STATISTICS_FOR} ${companyCount} ${translation.HID}`;
    };

    const companyCount = hidMap && Object.keys(hidMap).length;

    const noResult = () => companyCount === 0;

    return (
        <div className={classes.root}>
            { !isAnalyzeUser && (<Redirect to="/" />)}
            <Grid container spacing={2}>
                <Grid item xs={12} lg={8} xl={5}>
                    <Card className={classes.cardOverflowVisible}>
                        <CardContent>
                            <Typography gutterBottom variant="h5" component="h2">
                                {translation.COMPANY}
                            </Typography>
                            <CompanyStatsForm
                                handleClear={handleClear}
                                handleSubmit={handleSubmit}
                                handleUpdateFormField={handleUpdateFormField}
                                handleSelectCompanySuggestions={handleSelectCompanySuggestions}
                                selectedCompanySuggestions={selectedSuggestions}
                                locale={userPreferedLanguage}
                                searchActive={graphData !== null}
                                formState={formState}
                                translation={translation}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Divider className={classes.formResultDivider} light />
                </Grid>
                {noResult() && (
                    <Grid item xs={12} md={6}>
                        <Typography display="inline" variant="body2">
                            {translation.NO_RESULT_MESSAGE}
                        </Typography>
                    </Grid>
                )}
                {!noResult() && hidMap && (
                    <Grid item xs={12}>
                        <Typography variant="h5" component="h2">
                            {getHeader(companyCount)}
                        </Typography>
                    </Grid>
                )}
                {!noResult() && graphData && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={6}>
                                        <Typography gutterBottom variant="h6" component="h3">
                                            {translation.KPI}
                                        </Typography>
                                        <List disablePadding>
                                            {kpis.map(kpi => (
                                                <ListItem key={kpi.key}>
                                                    <ListItemIcon>
                                                        {kpi.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={kpi.number}
                                                        secondary={kpi.text}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Grid>
                                    {graphs.map(graph => (
                                        <Grid key={graph.key} item xs={12} lg={6}>
                                            <Typography gutterBottom variant="h6" component="h3">
                                                {graph.header}
                                                        :&nbsp;
                                                {graph.totalCount}
                                            </Typography>
                                            {isWidthUp('sm', width) && (
                                                <LineGraph
                                                    graphData={graph.graphData}
                                                    groupBy={groupBy}
                                                    handleGroupBy={handleGroupBy}
                                                    graphKeys={graph.graphKeys}
                                                    selectedGraphKeys={graph.selectedGraphKeys}
                                                    handleSelectGraphKeys={
                                                        graph.handleSelectGraphKeys
                                                    }
                                                    translation={translation}
                                                    isAdvancedUser={isAnalyzeUser}
                                                />
                                            )}
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {!noResult() && companyTableData
                && companyTableData.dataRows.length > 1
                && (
                    <Grid item xs={12} xl={6}>
                        <Card>
                            <CardContent>
                                <EnhancedTable
                                    tableTitle={translation.COMPANIES}
                                    tableActionText={translation.SEARCH}
                                    tableActionTooltip={translation.TOOLTIP_SEARCH_COMPANIES}
                                    handleTableAction={searchSelectedCompanies}
                                    rows={companyTableData.dataRows}
                                    cols={companyTableData.dataCols}
                                    selectableRows
                                    translation={translation}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )
                }
                {!noResult() && productTableData
                && (
                    <Grid item xs={12} xl={6}>
                        <Card>
                            <CardContent>
                                <EnhancedTable
                                    tableTitle={translation.SUMMARY}
                                    rows={productTableData.dataRows}
                                    cols={productTableData.dataCols}
                                    translation={translation}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )
                }
                {!noResult() && leadOriginData
                && (
                    <Grid item xs={12} sm={6} xl={3}>
                        <Card className={classes.card}>
                            <CardContent>
                                <Typography gutterBottom variant="h6" component="h3">
                                    {translation.LEAD_ORIGIN}
                                </Typography>
                                <DoughnutGraph graphData={leadOriginData} />
                            </CardContent>
                        </Card>
                    </Grid>
                )
                }
            </Grid>
        </div>
    );
};

CompanyStatsWrapper.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    hidMap: PropTypes.objectOf(PropTypes.any),
    handleClear: PropTypes.func.isRequired,
    handleGroupBy: PropTypes.func.isRequired,
    groupBy: PropTypes.string.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleUpdateFormField: PropTypes.func.isRequired,
    handleSelectCompanySuggestions: PropTypes.func.isRequired,
    handleSelectedLeadsGraphKeys: PropTypes.func.isRequired,
    handleSelectedClicksGraphKeys: PropTypes.func.isRequired,
    handleSelectedImpressionsGraphKeys: PropTypes.func.isRequired,
    formState: companyStatsFormStatePropTypes.isRequired,
    graphData: PropTypes.objectOf(PropTypes.any),
    selectedSuggestions: suggestionsPropType.isRequired,
    graphKeysLead: graphKeysPropTypes,
    preSelectedGraphKeysLead: graphKeysPropTypes,
    graphKeysClick: graphKeysPropTypes,
    preSelectedGraphKeysClick: graphKeysPropTypes,
    graphKeysImpression: graphKeysPropTypes,
    preSelectedGraphKeysImpression: graphKeysPropTypes,
    companyTableData: PropTypes.objectOf(PropTypes.any),
    productTableData: PropTypes.objectOf(PropTypes.any),
    leadOriginData: PropTypes.objectOf(PropTypes.any),
    width: PropTypes.string.isRequired,
    userPreferedLanguage: PropTypes.string.isRequired,
    totalImpressionCount: PropTypes.string,
    totalDetailImpressionCount: PropTypes.string,
    totalListImpressionCount: PropTypes.string,
    totalClickCount: PropTypes.string,
    totalListClickCount: PropTypes.string,
    totalListCTR: PropTypes.string,
    totalLeadCount: PropTypes.string,
    isAnalyzeUser: PropTypes.bool.isRequired,
};

CompanyStatsWrapper.defaultProps = {
    graphData: null,
    hidMap: null,
    companyTableData: null,
    productTableData: null,
    leadOriginData: null,
    totalImpressionCount: null,
    totalDetailImpressionCount: null,
    totalListImpressionCount: null,
    totalClickCount: null,
    totalListClickCount: null,
    totalListCTR: null,
    totalLeadCount: null,
    graphKeysLead: null,
    preSelectedGraphKeysLead: null,
    graphKeysClick: null,
    preSelectedGraphKeysClick: null,
    graphKeysImpression: null,
    preSelectedGraphKeysImpression: null,
};

export default withWidth()(withStyles(styles)(CompanyStatsWrapper));
